import { ARCADE_LIST, type ArcadeId, GAME_LIST, type GameId } from "@playpal/core";

/**
 * The games this child has starred, on this device.
 *
 * Local only, like everything else the browser remembers: favourites are a
 * preference, not a record, and a preference should not need an account. A
 * sibling on their own tablet gets their own stars, which is exactly what two
 * children sharing one catalogue would ask for.
 */

const FAVOURITES_KEY = "playpal.favourites";

/**
 * A starred thing is either a turn game or an arcade race, and the two live in
 * separate registries whose ids could one day collide — so the kind travels
 * with the id, the same way the picker keys its shelf.
 */
export type FavouriteKey = `game:${GameId}` | `arcade:${ArcadeId}`;

export function favouriteKey(entry: { kind: "game" | "arcade"; id: string }): FavouriteKey {
	return `${entry.kind}:${entry.id}` as FavouriteKey;
}

function knownKeys(): Set<string> {
	return new Set([
		...GAME_LIST.map((game) => `game:${game.id}`),
		...ARCADE_LIST.map((entry) => `arcade:${entry.id}`),
	]);
}

/** Every favourite, filtered against the catalogue so a retired game's star quietly disappears. */
export function favourites(): FavouriteKey[] {
	try {
		const raw = localStorage.getItem(FAVOURITES_KEY);
		const saved: unknown = raw ? JSON.parse(raw) : null;
		if (!Array.isArray(saved)) return [];
		const known = knownKeys();
		return saved.filter((key): key is FavouriteKey => typeof key === "string" && known.has(key));
	} catch {
		// Blocked or full storage just means no stars this time.
		return [];
	}
}

/** Stars an unstarred game, unstars a starred one, and returns the new list. */
export function toggleFavourite(key: FavouriteKey): FavouriteKey[] {
	const current = favourites();
	const next = current.includes(key) ? current.filter((kept) => kept !== key) : [...current, key];
	try {
		localStorage.setItem(FAVOURITES_KEY, JSON.stringify(next));
	} catch {
		// A star that will not stick is survivable; the toggle still works today.
	}
	return next;
}
